import {
    Box,
    Button,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import HeaderIcon from "/src/assets/FLWM-FULL LOGO DARK.svg";
import HeaderIconDark from "/src/assets/FLWM-FULL LOGO LIGHT.svg";
import GoogleIcon from "/src/assets/goggleIcon.png";
import MicrosoftLogo from "/src/assets/microsoftIcon.png";
import SalesforceLogo from "/src/assets/salesforceLogo.jpg";
import Footer from "../../components/common/Footer";
import { useConfig } from "../../config/ConfigProvider";
import { getOAuthProviders, startOAuthFlow, OAuthProvider } from "../../auth/oauthService";


export const MuiLogin = (): JSX.Element => {
    const theme = useTheme();
    const config = useConfig();
    const [oauthProviders, setOauthProviders] = useState<OAuthProvider[]>([]);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const hasSignedUp = localStorage.getItem("userSignedUp") === "true";
    
    // Redirect old domain to new domain
    useEffect(() => {
        if (window.location.hostname === 'jbs.evenflow.world') {
            const newUrl = window.location.href.replace('jbs.evenflow.world', 'flwm.evenflow.world');
            window.location.replace(newUrl);
        }
    }, []);
    
    const handleProviderClick = (provider: OAuthProvider) => {
        startOAuthFlow(provider.url);
    };

    // Load OAuth providers on mount
    useEffect(() => {
        if (!config?.apiBaseUrl) {
            setLoadingProviders(false);
            return;
        }
        
        const loadProviders = async () => {
            try {
                const providers = await getOAuthProviders(config.apiBaseUrl);
                setOauthProviders(providers);
            } catch (error) {
                console.error("Failed to fetch OAuth providers:", error);
                setOauthProviders([]);
            } finally {
                setLoadingProviders(false);
            }
        };
        loadProviders();
    }, [config?.apiBaseUrl]);

    const getProviderIcon = (providerName: string): string => {
        switch (providerName.toLowerCase()) {
            case "google":
                return GoogleIcon;
            case "microsoft":
                return MicrosoftLogo;
            case "salesforce":
                return SalesforceLogo;
            default:
                return "";
        }
    };

    const getProviderColor = (providerName: string): string => {
        switch (providerName.toLowerCase()) {
            case "google":
                return "#4285F4";
            case "microsoft":
                return "#00A4EF";
            case "salesforce":
                return "#00A1E0";
            default:
                return theme.palette.primary.main;
        }
    };
    
    const [timer, setTimer] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    useEffect(() => {
        const endDate = new Date('March 2, 2026 00:00:00').getTime();

        function updateTimer() {
            const now = new Date().getTime();
            const timeLeft = endDate - now;

            const days = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
            const hours = Math.max(0, Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
            const minutes = Math.max(0, Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)));
            const seconds = Math.max(0, Math.floor((timeLeft % (1000 * 60)) / 1000));

            setTimer({ days, hours, minutes, seconds });
        }

        updateTimer(); // initial call
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                px: 3,
                bgcolor: "background.default",
            }}
        >
            {/* Header */}
            <Box
                component="header"
                sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: "auto", sm: "var(--header-height)" }, // auto on mobile, fixed on desktop
                    bgcolor: "background.paper",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: { xs: 0, sm: 4 },
                    py: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                }}
            >
                <Box
                    component="img"
                    src={theme.palette.mode === 'light' ? HeaderIcon : HeaderIconDark}
                    alt="NeuAI icon"
                    sx={{
                        width: 180,
                        top: "19px",
                        left: "20px",
                    }}
                />

                <Stack
                    direction="row"
                    spacing={0} // <-- Remove gap between children
                    alignItems="center"
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        '& .timer-container': {
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                        },
                        '& .timer-container span, & .timer-container p, & .timer-container div': {
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                        },
                        '& .opt-in-button': {
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                        },
                    }}
                >
                    <div
                        className="timer-container"
                        style={{ width: '100%', padding: "0px" }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' }, // stacked on mobile, inline on larger screens
                                alignItems: { xs: 'center', sm: 'center' },
                                justifyContent: { xs: 'center', sm: 'flex-end' },
                                width: '100%',
                                gap: 0,
                            }}
                        >
                            <Box
                                className="rowOne"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',           // vertical centering
                                    justifyContent: 'center',       // horizontal centering
                                    width: { xs: '100%', sm: 'auto' },
                                    mb: { xs: 1, sm: 0 },
                                }}
                            >
                                <img
                                    src="https://img.icons8.com/ios-filled/50/000000/clock.png"
                                    alt="Clock Icon"
                                    className="clock-icon"
                                    width={20}
                                    height={20}
                                    style={{ 
                                        marginRight: 8,
                                        filter: 'invert(1)'
                                    }}
                                />
                                <Box
                                    component="span"
                                    sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                        marginRight: { xs: "0px", sm: "5px" }
                                    }}
                                >
                                    Beta March 2nd
                                </Box>
                            </Box>
                            <Box
                                className="rowTwo"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    width: { xs: '100%', sm: 'auto' },
                                    mb: { xs: 1, sm: 0 },
                                }}
                            >
                                <Box
                                    className="timer"
                                    sx={{
                                        display: 'flex',
                                        gap: 0,
                                        width: { xs: '100%', sm: 'auto' },
                                        justifyContent: { xs: 'space-between', sm: 'flex-start' },
                                    }}
                                >
                                    <Box id="days" sx={{ textAlign: "center", width: "100%" }}>{timer.days}<p>DAYS</p></Box>
                                    <Box id="hours" sx={{ textAlign: "center", width: "100%" }}>{timer.hours}<p>HOURS</p></Box>
                                    <Box id="minutes" sx={{ textAlign: "center", width: "100%" }}>{timer.minutes}<p>MINS</p></Box>
                                    <Box id="seconds" sx={{ textAlign: "center", width: "100%" }}>{timer.seconds}<p>SECS</p></Box>
                                </Box>
                            </Box>

                        </Box>
                    </div>

                </Stack>
            </Box>
            {/* Main Content */}
            <Stack
                sx={{
                    flex: 1,
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    maxHeight: {
                        xs: "none",
                        sm: "calc(100vh - var(--header-height))"
                    },
                    margin: 'auto',
                    overflow: 'auto',
                    height: '100%',
                    width: '100%',
                    textAlign: "center",
                    background: "background.main",
                }}
            >
                <Box sx={{ marginTop: '5%', textAlign: "center" }}>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{
                            fontSize: { xs: '26px', sm: '36px' },
                            fontWeight: 700,
                            mb: 2,
                        }}
                    >
                        Fight Less, Win More.
                    </Typography>
                    <Typography
                        variant="h4"
                        component="h2"
                        align="center"
                        sx={{
                            fontSize: { xs: '16px', sm: '24px' },
                            fontWeight: 400,
                            mb: 6,
                        }}
                    >
                        sign in to access the future of negotiation prep.
                    </Typography>

                    {/* Modern OAuth Login Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            maxWidth: 420,
                            mx: 'auto',
                            p: 5,
                            borderRadius: 4,
                            background: 'linear-gradient(135deg, #e50c20 0%, #3a2a2e 50%)',
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 1,
                                fontWeight: 700,
                                textAlign: 'center',
                                color: '#fff',
                            }}
                        >
                            Sign In
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 4,
                                textAlign: 'center',
                                color: 'rgba(255, 255, 255, 0.9)',
                            }}
                        >
                            Choose your preferred sign-in method
                        </Typography>

                        {loadingProviders ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <Typography>Loading sign-in options...</Typography>
                            </Box>
                        ) : !Array.isArray(oauthProviders) || oauthProviders.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography color="error" gutterBottom>
                                    Unable to connect to authentication service
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Please ensure the backend is running on {config.apiBaseUrl}
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={2.5}>
                                {oauthProviders
                                    .filter((provider) => provider.name.toLowerCase() === 'google' || provider.name.toLowerCase() === 'microsoft')
                                    .map((provider) => (
                                    <Button
                                        key={provider.name}
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleProviderClick(provider)}
                                        sx={{
                                            bgcolor: '#000000 !important',
                                            background: '#000000 !important',
                                            backgroundImage: 'none !important',
                                            color: '#ffffff !important',
                                            border: '1px solid #e50c20',
                                            "&:hover": {
                                                bgcolor: '#1a1a1a !important',
                                                background: '#1a1a1a !important',
                                                backgroundImage: 'none !important',
                                                color: "#ffffff !important",
                                                border: '1px solid #e50c20',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 8px 20px rgba(229, 12, 32, 0.4)',
                                            },
                                            textTransform: "none",
                                            py: 1.75,
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                            borderRadius: 2.5,
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            gap: 2,
                                            px: 3,
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {getProviderIcon(provider.name) && (
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={getProviderIcon(provider.name)}
                                                    alt={`${provider.displayName} logo`}
                                                    sx={{
                                                        maxWidth: 24,
                                                        maxHeight: 24,
                                                        width: "auto",
                                                        height: "auto",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box component="span">Continue with {provider.displayName}</Box>
                                    </Button>
                                ))}
                            </Stack>
                        )}
                        <Typography 
                            variant="caption"
                            sx={{ 
                                display: "block", 
                                pt: 4,
                                fontSize: '0.75rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                            }}
                        >
                            By signing in, you agree to our Terms of Service
                        </Typography>
                    </Paper>
                </Box>
                <Outlet />
            </Stack>
            {/* Footer */}
            <Box sx={{ width: '100%' }}>
                <Footer />
            </Box>
        </Box>
    );
};

